import * as types from '../mutation-types';
import IntelligenceDashboardAPI from '../../api/intelligenceDashboard';

const state = {
  uiFlags: {
    isFetchingSummary: false,
    isFetchingConversationStates: false,
    isFetchingOriginBreakdown: false,
    isFetchingAgentManagement: false,
    isFetchingDrillDown: false,
    isFetchingConfig: false,
    isFetchingLabelsTop: false,
    isFetchingByChannelWeek: false,
    isFetchingByMonth: false,
    isFetchingHistoryByDay: false,
    isFetchingAvgResponseTime: false,
    isFetchingFunnel: false,
  },
  summary: {},
  conversationStates: {},
  originBreakdown: [],
  agentManagement: [],
  drillDown: { data: [], meta: {} },
  config: {},
  labelsTop: [],
  byChannelWeek: { weeks: [], series: [] },
  byMonth: { months: [], series: [] },
  historyByDay: [],
  avgResponseTime: [],
  funnel: [],
};

const getters = {
  getIntelligenceDashboardSummary: _state => _state.summary,
  getIntelligenceDashboardConversationStates: _state =>
    _state.conversationStates,
  getIntelligenceDashboardOriginBreakdown: _state => _state.originBreakdown,
  getIntelligenceDashboardAgentManagement: _state => _state.agentManagement,
  getIntelligenceDashboardDrillDown: _state => _state.drillDown,
  getIntelligenceDashboardConfig: _state => _state.config,
  getIntelligenceDashboardLabelsTop: _state => _state.labelsTop,
  getIntelligenceDashboardByChannelWeek: _state => _state.byChannelWeek,
  getIntelligenceDashboardByMonth: _state => _state.byMonth,
  getIntelligenceDashboardHistoryByDay: _state => _state.historyByDay,
  getIntelligenceDashboardAvgResponseTime: _state => _state.avgResponseTime,
  getIntelligenceDashboardFunnel: _state => _state.funnel,
  getIntelligenceDashboardUIFlags: _state => _state.uiFlags,
};

const latestRequest = {};

const buildFetcher = ({ flag, mutation, apiMethod }) => {
  return ({ commit }, params) => {
    const token = (latestRequest[flag] || 0) + 1;
    latestRequest[flag] = token;
    commit(types.default.TOGGLE_INTELLIGENCE_DASHBOARD_LOADING, {
      key: flag,
      value: true,
    });
    return IntelligenceDashboardAPI[apiMethod](params)
      .then(response => {
        if (latestRequest[flag] !== token) return;
        commit(types.default[mutation], response.data);
      })
      .finally(() => {
        if (latestRequest[flag] !== token) return;
        commit(types.default.TOGGLE_INTELLIGENCE_DASHBOARD_LOADING, {
          key: flag,
          value: false,
        });
      });
  };
};

export const actions = {
  fetchSummary: buildFetcher({
    flag: 'isFetchingSummary',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_SUMMARY',
    apiMethod: 'getSummary',
  }),
  fetchConversationStates: buildFetcher({
    flag: 'isFetchingConversationStates',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_CONVERSATION_STATES',
    apiMethod: 'getConversationStates',
  }),
  fetchOriginBreakdown: buildFetcher({
    flag: 'isFetchingOriginBreakdown',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_ORIGIN_BREAKDOWN',
    apiMethod: 'getOriginBreakdown',
  }),
  fetchAgentManagement: buildFetcher({
    flag: 'isFetchingAgentManagement',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_AGENT_MANAGEMENT',
    apiMethod: 'getAgentManagement',
  }),
  fetchLabelsTop: buildFetcher({
    flag: 'isFetchingLabelsTop',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_LABELS_TOP',
    apiMethod: 'getLabelsTop',
  }),
  fetchByChannelWeek: buildFetcher({
    flag: 'isFetchingByChannelWeek',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_BY_CHANNEL_WEEK',
    apiMethod: 'getByChannelWeek',
  }),
  fetchByMonth: buildFetcher({
    flag: 'isFetchingByMonth',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_BY_MONTH',
    apiMethod: 'getByMonth',
  }),
  fetchHistoryByDay: buildFetcher({
    flag: 'isFetchingHistoryByDay',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_HISTORY_BY_DAY',
    apiMethod: 'getHistoryByDay',
  }),
  fetchAvgResponseTime: buildFetcher({
    flag: 'isFetchingAvgResponseTime',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_AVG_RESPONSE_TIME',
    apiMethod: 'getAvgResponseTime',
  }),
  fetchFunnel: buildFetcher({
    flag: 'isFetchingFunnel',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_FUNNEL',
    apiMethod: 'getFunnel',
  }),
  fetchDrillDown: buildFetcher({
    flag: 'isFetchingDrillDown',
    mutation: 'SET_INTELLIGENCE_DASHBOARD_DRILL_DOWN',
    apiMethod: 'getDrillDown',
  }),

  fetchConfig({ commit }) {
    commit(types.default.TOGGLE_INTELLIGENCE_DASHBOARD_LOADING, {
      key: 'isFetchingConfig',
      value: true,
    });
    return IntelligenceDashboardAPI.getConfig()
      .then(response => {
        commit(types.default.SET_INTELLIGENCE_DASHBOARD_CONFIG, response.data);
      })
      .finally(() => {
        commit(types.default.TOGGLE_INTELLIGENCE_DASHBOARD_LOADING, {
          key: 'isFetchingConfig',
          value: false,
        });
      });
  },

  updateConfig({ commit }, configData) {
    return IntelligenceDashboardAPI.updateConfig(configData).then(response => {
      commit(types.default.SET_INTELLIGENCE_DASHBOARD_CONFIG, response.data);
    });
  },
};

const mutations = {
  [types.default.SET_INTELLIGENCE_DASHBOARD_SUMMARY](_state, data) {
    _state.summary = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_CONVERSATION_STATES](_state, data) {
    _state.conversationStates = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_ORIGIN_BREAKDOWN](_state, data) {
    _state.originBreakdown = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_AGENT_MANAGEMENT](_state, data) {
    _state.agentManagement = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_DRILL_DOWN](_state, data) {
    _state.drillDown = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_CONFIG](_state, data) {
    _state.config = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_LABELS_TOP](_state, data) {
    _state.labelsTop = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_BY_CHANNEL_WEEK](_state, data) {
    _state.byChannelWeek = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_BY_MONTH](_state, data) {
    _state.byMonth = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_HISTORY_BY_DAY](_state, data) {
    _state.historyByDay = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_AVG_RESPONSE_TIME](_state, data) {
    _state.avgResponseTime = data;
  },
  [types.default.SET_INTELLIGENCE_DASHBOARD_FUNNEL](_state, data) {
    _state.funnel = data;
  },
  [types.default.TOGGLE_INTELLIGENCE_DASHBOARD_LOADING](_state, { key, value }) {
    _state.uiFlags[key] = value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
